import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";
import { FormCloudEnvironment } from "~/models/Commons ";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLDialog } from "../../components/commons/dialog/SimplDialog";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { UseFormCloudEnvironment } from "./UseFormCloudEnvironment";
import { getProviders } from "~/Utils/Utils";

interface Prop {
  onCloseModal: () => void;
  editedItem: FormCloudEnvironment | null;
  setConfig: Function;
  title?: string;
  refetch: () => void;
}

export const EditCloudEnvironment = ({
  onCloseModal,
  editedItem,
  setConfig,
  refetch,
  title,
}: Prop) => {
  const { handleSubmit, control, errors, updateForm, setValue, provider } =
    UseFormCloudEnvironment(onCloseModal, refetch, editedItem?.id!);
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);

  const fetchProviderList = () => {
    setCloudProviderList(getProviders(provider));
  };
  useEffect(() => {
    fetchProviderList();
  }, [provider]);

  useEffect(() => {
    if (editedItem?.id) {
      setValue("environmentName", editedItem.environmentName ?? "");
      setValue(
        "environmentDescription",
        editedItem.environmentDescription ?? ""
      );
      setValue("datacenterName", editedItem.datacenterName ?? "");
      setValue("datacenterDescription", editedItem.datacenterDescription ?? "");
      setValue("location", editedItem.location ?? "");
      setValue("iac", editedItem.iac ?? "");
      setValue("authenticationConfig", "");
      const formCloudEnvironmentResponse: any = editedItem;
      const providers = getProviders(provider);
      const selectedProvider: any = providers?.find(
        (p) => p.id === formCloudEnvironmentResponse!.cloudProvider.id
      );

      if (selectedProvider) {
        setValue("cloudProviderId", {
          label: selectedProvider.cloudProviderName,
          value: selectedProvider.id,
        });
      }
    }
  }, [editedItem, setValue, provider]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    handleSubmit(updateForm)(event);
    setConfig({
      key: "",
      direction: LABELS.ASC,
    });
  };

  const renderButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label="Cancel"
          secondary
          buttonHandlerAction={onCloseModal}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized label={LABELS.Update} primary />
      </div>
    </>
  );

  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={title!}
      actions={renderButtons()}
      onSubmit={handleFormSubmit}
    >
      <div className="flex justify-center">
        <div className="w-[500px]">
          <div className="flex flex-col w-full gap-y-4">
            <SIMPLInput
              name="environmentName"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: LABELS.NameRequired,
                },
              }}
              label={LABELS.Name}
              placeholder={LABELS.Name}
              required
              id="environmentName"
              fontWeight="bold"
              errorLabel={errors.environmentName?.message}
            />

            <SIMPLInput
              name="environmentDescription"
              control={control}
              label={LABELS.Description}
              id="environmentDescription"
              fontWeight="bold"
              errorLabel={errors.environmentDescription?.message}
              placeholder={LABELS.Description}
            />

            <SIMPLInput
              name="datacenterName"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: LABELS.DataCenterRequired,
                },
              }}
              label={LABELS.DataCenterName}
              placeholder={LABELS.DataCenterName}
              required
              id="datacenterName"
              fontWeight="bold"
              errorLabel={errors.datacenterName?.message}
            />

            <SIMPLInput
              name="datacenterDescription"
              control={control}
              label={LABELS.DataCenterDescription}
              placeholder={LABELS.DataCenterDescription}
              id="datacenterDescription"
              fontWeight="bold"
              errorLabel={errors.datacenterDescription?.message}
            />

            <SIMPLInput
              name="location"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: LABELS.LocationRequired,
                },
              }}
              label={LABELS.Location}
              required
              id="location"
              fontWeight="bold"
              errorLabel={errors.location?.message}
              placeholder={LABELS.Location}
            />

            <SIMPLInput
              name="iac"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: LABELS.IacRequired,
                },
              }}
              label={LABELS.Iac}
              required
              id="iac"
              fontWeight="bold"
              errorLabel={errors.iac?.message}
              placeholder={LABELS.Iac}
            />

            {/* Cloud Provider dropdown */}
            <div className="h-auto">
              <Controller
                name="cloudProviderId"
                control={control}
                rules={{ required: MESSAGES.MessageCloudProviderRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="cloudProvider"
                    label="Cloud Provider"
                    options={cloudProviderList?.map((cloudProviderName) => ({
                      label: cloudProviderName.cloudProviderName,
                      value: cloudProviderName.id,
                    }))}
                    isRequired
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.cloudProviderId?.message as string}
                    placeholder={LABELS.CloudProviderWithSpace}
                  />
                )}
              />
            </div>

            <SIMPLInput
              name="authenticationConfig"
              type="textarea"
              rows={4}
              control={control}
              label={LABELS.AuthenConfig}
              id="authenticationConfig"
              fontWeight="bold"
              rules={{
                validate: (value: string) => {
                  if (value == null || value.trim() === "") {
                    return true;
                  }
                  try {
                    JSON.parse(value);
                    return true;
                  } catch {
                    return LABELS.InvalidJsonFormat;
                  }
                },
              }}
              errorLabel={errors.authenticationConfig?.message}
              placeholder={LABELS.JsonAlreadyExits}
            />
          </div>
        </div>
      </div>
    </SIMPLDialog>
  );
};
